/**
 * @fileoverview Schema definitions for rule builder validation
 * Provides Zod schemas for validating rule conditions and groups
 */

import {
  ComputationType,
  DecisionType,
  GroupOperator,
  ComparatorType
} from "@/graphql/__generated__/graphql";
import { TFunction } from "i18next";
import { z } from "zod";
import { allowsNullValue, percentageInputKeys } from "../constants";

/**
 * Creates and returns Zod schemas for rule validation
 *
 * @param {TFunction} t - Translation function for localized error messages
 * @returns {Object} Object containing the defined schemas
 */
export const createRuleSchema = (t: TFunction) => {
  /**
   * Schema for validating individual rule conditions
   * Ensures required fields are present and values are within acceptable ranges
   */
  const RuleConditionSchema = z.object({
    operator: z.enum(ComparatorType).optional(),
    value: z.preprocess((val) => {
      // Handle null value explicitly
      if (val === "null") return val;
      // Convert to string, but only if the value is not undefined/null
      return val != null ? String(val) : undefined;
    }, z.string().optional()),
    computationType: z.enum(ComputationType).optional(),
    field: z.string().optional(),
  }).refine(data => data.operator !== undefined, {
    message: t("schemas:rulebuilder.operator.required"),
    path: ["operator"]
  }).refine(data => data.computationType !== undefined, {
    message: t("schemas:rulebuilder.computationType.required"),
    path: ["computationType"]
  }).refine(data => {
    // Skip value validation for IsNull operator
    if (data.operator === ComparatorType.IsNull && data.computationType && allowsNullValue(data.computationType)) {
      return true;
    }

    // Require value for all other cases
    return data.value !== undefined && data.value !== "" && data.value !== "undefined" && !isNaN(Number(data.value));
  }, {
    message: t("schemas:rulebuilder.value.required"),
    path: ["value"]
  }).refine(data => {
    if (data.computationType && allowsNullValue(data.computationType) && data.operator === ComparatorType.IsNull) {
      return true;
    }

    if (data.computationType && percentageInputKeys.includes(data.computationType) && data.value) {
      const numValue = parseFloat(data.value);
      return !isNaN(numValue) && numValue >= 0 && numValue <= 100;
    }
    return true;
  }, {
    message: t("schemas:rulebuilder.value.range"),
    path: ["value"]
  });

  /**
   * Schema for validating condition groups
   * Uses recursive definition to support nested condition groups
   */
  const ConditionGroupSchema: z.ZodType<any> = z.lazy(() =>
    z.object({
      operator: z.enum(GroupOperator),
      conditions: z.array(RuleConditionSchema),
      childGroups: z.array(ConditionGroupSchema),
    })
  );

  /**
   * Schema for validating the entire rule creation form
   * Includes rule metadata and the root condition group
   */
  const CreateRuleSchema = z.object({
    name: z.string().min(1, t("schemas:rulebuilder.name.required")),
    description: z.string().max(255, t("schemas:rulebuilder.description.maxLength")).optional(),
    decisionType: z.enum(DecisionType),
    rootGroup: ConditionGroupSchema,
  });

  return {
    RuleConditionSchema,
    ConditionGroupSchema,
    CreateRuleSchema
  };
};
